import { Lang } from "@/lib/i18n";
import { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "ReverseDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Dutch Weave Filter Cloth",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Reverse Dutch Weave Wire Mesh",
      lead: "Engineered with dense, fine warp wires paired with heavy, widely spaced shute wires, Reverse Dutch Weave delivers extreme longitudinal tensile strength for continuous screen belts and high-pressure polymer melt filtration.",
      secondaryCta: {
        label: "View All Filter Cloth",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
        alt: "Reverse Dutch Weave stainless steel filter cloth macro view",
      },
      cardNote: "High-tensile longitudinal belt media",
      cardLink: {
        label: "Specifications",
        to: "/ReverseDutchWeavePage#specifications",
      },
      specs: [
        { label: "Mesh Range", value: "72×15 – 325×38" },
        { label: "Retention Rating", value: "10 – 300 µm nominal" },
        { label: "Key Trait", value: "High longitudinal tensile strength" },
        { label: "Standard Alloys", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "High-Tensile Continuous Belts",
      heading: "Inverted Wire Ratio for Automatic Screen Changers",
      paragraphs: [
        "Reverse Dutch Weave (also known as panzertresse or reverse plain dutch wire cloth) inverts the standard Dutch wire ratio. In this construction, a high count of fine warp wires is placed under tension in the machine direction, while heavier, thicker wires are woven across the weft at wider intervals.",
        "Because the closely packed fine wires run parallel to the length of the roll, the fabric exhibits exceptional tensile breaking strength in the longitudinal direction. This specific mechanical property makes Reverse Dutch cloth the international standard media for continuous auto-screen changers used in plastic recycling, blown film extrusion, and polymer compounding.",
        "Under intense polymer melt pressure, the transverse heavy wires serve as rigid internal cross-ribs that prevent elongation and necking, while the tightly packed fine warp wires establish precise particle retention from 10 µm to 300 µm. MutualFilter supplies reverse Dutch mesh in precision-slit continuous rolls, custom coils with ultrasonic deburred edges, and reinforced stamped belts.",
      ],
      keyPoints: [
        "Dense warp wires paired with heavier spaced weft wires deliver exceptional longitudinal tensile strength for continuous screen belts and high-pressure polymer melt filtration.",
        "Inverted wire geometry resists mechanical stretching and necking under high extrusion drive force.",
        "Standard manufacturing mesh range from 72×15 to 325×38 with nominal retention ratings from 10 µm to 300 µm.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
        alt: "Stainless steel reverse dutch weave wire cloth micro detail",
      },
    },
    specTable: {
      heading: "Reverse Dutch Weave Standard Specifications",
      intro: "Manufacturing specifications for industrial stainless steel reverse dutch weave wire cloth conforming to ASTM E2016 and ISO 9044.",
      columns: [
        { key: "mesh", label: "Mesh (Warp × Weft)", align: "left" },
        { key: "warpWire", label: "Warp Wire (mm)", align: "right" },
        { key: "weftWire", label: "Weft Wire (mm)", align: "right" },
        { key: "retention", label: "Nominal Micron Rating (µm)", align: "right" },
        { key: "standard", label: "Compliance Standard", align: "right" },
      ],
      rows: [
        {
          id: "row-rd-1",
          mesh: "72 × 15",
          warpWire: "0.28",
          weftWire: "0.45",
          retention: "250 – 300",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-2",
          mesh: "132 × 16",
          warpWire: "0.20",
          weftWire: "0.40",
          retention: "130 – 150",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-3",
          mesh: "152 × 24",
          warpWire: "0.18",
          weftWire: "0.30",
          retention: "90 – 110",
          standard: "ISO 9044",
        },
        {
          id: "row-rd-4",
          mesh: "260 × 40",
          warpWire: "0.10",
          weftWire: "0.15",
          retention: "40 – 50",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-5",
          mesh: "325 × 38",
          warpWire: "0.08",
          weftWire: "0.13",
          retention: "10 – 20",
          standard: "ASTM E2016",
        },
      ],
      caption: "Nominal retention determined by glass bead challenge and bubble point correlation per ASTM E2016.",
    },
    features: {
      heading: "Key Operational Attributes",
      intro: "Structural benefits of reverse Dutch wire cloth in demanding mechanical drive applications.",
      items: [
        {
          title: "Extreme Tensile Resistance",
          text: "Closely aligned longitudinal warp wires provide the mechanical pull strength necessary to index smoothly through hydraulic screen changer clamps without tearing.",
        },
        {
          title: "Transverse Anti-Necking Ribs",
          text: "Heavy cross-shute wires maintain continuous belt width under intense polymer melt pressure, preventing side leakage and seal failures.",
        },
        {
          title: "Clean Edge Slitting",
          text: "Precision ultrasonic slitting and deburring produce clean, burr-free roll edges for jam-free feeding in automated extrusion equipment.",
        },
        {
          title: "Corrosion-Resistant Alloys",
          text: "Woven from certified AISI 304, 304L, 316, and 316L stainless steel wires to withstand high processing temperatures and corrosive polymers.",
        },
      ],
    },
    related: {
      heading: "Explore Related Filter Cloth Variants",
      intro: "Compare Reverse Dutch Weave with alternative weave constructions across the Dutch weave family.",
      cards: [
        {
          title: "Plain Dutch Weave",
          text: "Heavy warp wires paired with compacted fine shute wires produce uniform triangular pores for predictable particulate retention and high cake discharge.",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "Plain Dutch Weave wire cloth",
          },
          badge: "8×85 – 80×400 (to 165×1200)",
          linkLabel: "View Plain Dutch",
        },
        {
          title: "Twill Dutch Weave",
          text: "Twilled warp wire arrangement allows double the weft density with zero direct line of sight, delivering micro-retention down to 1 micron under severe differential pressures.",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "Twill Dutch Weave wire cloth",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "View Twill Dutch",
        },
        {
          title: "Filter Cloth Overview",
          text: "Comprehensive family overview detailing Dutch weave engineering characteristics, alloy selections, and fabrication options.",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Filter cloth overview",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "View Parent Page",
        },
      ],
    },
    cta: {
      id: "reverse-dutch-cta",
      eyebrow: "Continuous Screen Belt Media",
      heading: "Order Precision Reverse Dutch Weave Wire Mesh",
      lead: "MutualFilter supplies slit coils, continuous belts, and custom filtration media manufactured to your operational dimensions.",
      secondaryLabel: "Explore Filter Cloth",
      secondaryTo: "/FilterClothPage",
      note: "Material test reports and compliance certificates available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "ReverseDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "荷兰编织工业滤布",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "反向荷兰编织丝网",
      lead: "采用高密度紧排细经丝跨越较粗的间距纬丝，反向荷兰编织（反向席型网/带网）具备卓越的纵向抗拉强度，专为自动换网器连续过滤带及高温高压聚合物熔体挤出设计。",
      secondaryCta: {
        label: "查看滤布总览",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
        alt: "不锈钢反向荷兰编织滤布微距拍摄",
      },
      cardNote: "高抗拉连续走网滤带介质",
      cardLink: {
        label: "技术参数表",
        to: "/ReverseDutchWeavePage#specifications",
      },
      specs: [
        { label: "目数范围", value: "72×15 – 325×38" },
        { label: "公称精度", value: "10 – 300 µm nominal" },
        { label: "机械特性", value: "高纵向抗拉拉力" },
        { label: "标准材质", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "高抗拉连续过滤带专用网",
      heading: "反向线径配比，满足自动换网器走网抗拉需求",
      paragraphs: [
        "反向荷兰编织（俗称反向密纹网或铠装席型网 Panzertresse）颠覆了传统荷兰编织的线径比例。在该结构中，大量高密度的细经丝沿织机走网纵向受拉排布，而较粗直径的纬丝则以较宽间距横向穿行交织。",
        "由于密排细金属丝均平行于网卷的展开长度方向，该织物展现出极高的纵向抗拉破断强度。这一力学特征使反向荷兰网成为塑料回收造粒、吹膜及工程塑料造粒设备中自动走网换网器（Auto-Screen Changer）的标准过滤带介质。",
        "在高达数十兆帕的熔体背压下，粗纬丝起到横向刚性支撑骨架的作用，防止走网时网面发生拉伸变窄（缩颈）；而纵向紧排的细经丝则精准提供 10 µm 至 300 µm 的熔体杂质拦截。MutualFilter 供应精密分切的连续走网带、超声波去毛刺卷带及加强型冲压带。",
      ],
      keyPoints: [
        "高密度紧排细经丝跨越较粗的间距纬丝，提供出色的纵向抗拉强度，专为自动换网器连续过滤带与高温高压聚合物熔体挤出设计。",
        "独特的反向力学结构在高牵引力与聚合物熔体挤压下有效防止网带缩颈变形。",
        "标准制造目数覆盖 72×15 至 325×38，公称截留精度覆盖 10 µm 至 300 µm。",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
        alt: "不锈钢反向荷兰编织过滤带微观细节",
      },
    },
    specTable: {
      heading: "反向荷兰编织常用技术规格表",
      intro: "符合 ASTM E2016 及 ISO 9044 标准的不锈钢反向荷兰编织标准制造规格代表数据。",
      columns: [
        { key: "mesh", label: "目数 (经 × 纬)", align: "left" },
        { key: "warpWire", label: "经丝丝径 (mm)", align: "right" },
        { key: "weftWire", label: "纬丝丝径 (mm)", align: "right" },
        { key: "retention", label: "公称精度 (µm)", align: "right" },
        { key: "standard", label: "执行标准", align: "right" },
      ],
      rows: [
        {
          id: "row-rd-1",
          mesh: "72 × 15",
          warpWire: "0.28",
          weftWire: "0.45",
          retention: "250 – 300",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-2",
          mesh: "132 × 16",
          warpWire: "0.20",
          weftWire: "0.40",
          retention: "130 – 150",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-3",
          mesh: "152 × 24",
          warpWire: "0.18",
          weftWire: "0.30",
          retention: "90 – 110",
          standard: "ISO 9044",
        },
        {
          id: "row-rd-4",
          mesh: "260 × 40",
          warpWire: "0.10",
          weftWire: "0.15",
          retention: "40 – 50",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-5",
          mesh: "325 × 38",
          warpWire: "0.08",
          weftWire: "0.13",
          retention: "10 – 20",
          standard: "ASTM E2016",
        },
      ],
      caption: "公称截留精度符合 ASTM E2016 玻璃微珠与气泡点标准测定数据。",
    },
    features: {
      heading: "力学与工程核心优势",
      intro: "反向荷兰编织滤带在机械高牵引工况下的技术优势。",
      items: [
        {
          title: "卓越的纵向破断抗拉力",
          text: "顺应走网方向紧密排列的细经丝，能够承受液压夹具的大拉力步进拖拽而不发生断带。",
        },
        {
          title: "横向粗纬丝抗缩颈支撑",
          text: "高强度粗纬丝在高温高压熔体挤压下保持网宽恒定，避免由于网面收缩引起熔体侧向泄漏。",
        },
        {
          title: "高精度超声波切边",
          text: "带材边缘经过严格的精密分切与去毛刺工艺，确保在全自动换网器导向槽内顺畅平稳推进。",
        },
        {
          title: "高温耐蚀不锈钢",
          text: "采用精炼 AISI 304、304L、316 及 316L 不锈钢材料，在高温熔融树脂中无析出与腐蚀。",
        },
      ],
    },
    related: {
      heading: "相关荷兰编织产品系列",
      intro: "对比反向荷兰编织与同系列其它编织结构的技术特性。",
      cards: [
        {
          title: "平纹荷兰编织",
          text: "粗经丝配合紧密敲紧的细纬丝形成规则三角微孔流道，兼具稳定的固体颗粒拦截精度与良好的表面滤饼剥离特性。",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "平纹荷兰编织工业滤布",
          },
          badge: "8×85 – 80×400 (最高至 165×1200)",
          linkLabel: "查看平纹荷兰编织",
        },
        {
          title: "斜纹荷兰编织",
          text: "结合斜纹走线与双向压紧的极高纬丝密度，形成无直射光透孔的曲折立体通道，在高压差恶劣工况下稳定实现低至 1 微米精密截留。",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "斜纹荷兰编织工业滤布",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "查看斜纹荷兰编织",
        },
        {
          title: "工业滤布总览",
          text: "查看荷兰编织工业滤布家族整体技术对比、材质选择指南及深加工形态。",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "工业滤布产品总览",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "返回上级页面",
        },
      ],
    },
    cta: {
      id: "reverse-dutch-cta",
      eyebrow: "自动换网过滤带",
      heading: "获取不锈钢反向荷兰编织网带技术报价",
      lead: "MutualFilter 提供连续走网带卷、定长滤带及超声波切边定制等多种供货方案。",
      secondaryLabel: "返回工业滤布总览",
      secondaryTo: "/FilterClothPage",
      note: "材质检验报告可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "ReverseDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Tela Filtrante Dutch",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malla Reverse Dutch Weave",
      lead: "Con alambres de urdimbre finos densamente agrupados cruzados por alambres de trama gruesos espaciados, el tejido Reverse Dutch proporciona una resistencia extrema a la tracción longitudinal para cambiadores continuos de mallas en extrusión.",
      secondaryCta: {
        label: "Ver Tela Filtrante",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
        alt: "Detalle de malla Reverse Dutch Weave en acero inoxidable",
      },
      cardNote: "Cintas de alta resistencia longitudinal",
      cardLink: {
        label: "Especificaciones",
        to: "/ReverseDutchWeavePage#specifications",
      },
      specs: [
        { label: "Rango de Malla", value: "72×15 – 325×38" },
        { label: "Retención Nominal", value: "10 – 300 µm nominal" },
        { label: "Propiedad Mecánica", value: "Alta resistencia longitudinal" },
        { label: "Aleaciones", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Cintas Continuas de Alta Resistencia",
      heading: "Relación de Alambres Invertida para Extrusión Continua",
      paragraphs: [
        "El tejido Reverse Dutch (reps inverso o panzertresse) invierte la relación convencional de los alambres. En esta estructura, un elevado número de alambres finos de urdimbre se orienta longitudinalmente bajo tensión, mientras que alambres más gruesos se entrelazan en la trama a intervalos espaciados.",
        "Dado que los alambres finos compactados corren paralelamente a la longitud de la bobina, la tela presenta una resistencia a la tracción longitudinal excepcional. Esta propiedad hace que el tejido Reverse Dutch sea el estándar internacional para cambiadores continuos en reciclaje de plásticos y extrusión de polímeros.",
        "Bajo las elevadas presiones del polímero fundido, los alambres transversales gruesos actúan como nervaduras que evitan el estrechamiento lateral (necking), asegurando una retención de 10 µm a 300 µm. MutualFilter suministra rollos continuos cortados de precisión con bordes limpios sin rebabas.",
      ],
      keyPoints: [
        "Urdidumbre de alta densidad combinada con alambres de trama más gruesos ofrece gran resistencia a la tracción para cambiadores de pantalla continuos y extrusión.",
        "Estructura mecánica invertida que evita el estrechamiento y deformación bajo alta fuerza de tracción.",
        "Rango de fabricación de 72×15 a 325×38 con retenciones nominales de 10 µm a 300 µm.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
        alt: "Superficie de tejido Reverse Dutch para cambiadores continuos",
      },
    },
    specTable: {
      heading: "Especificaciones Estándar Reverse Dutch",
      intro: "Datos de mallas metálicas Reverse Dutch de acero inoxidable conformes a ASTM E2016 e ISO 9044.",
      columns: [
        { key: "mesh", label: "Malla (Urdimbre × Trama)", align: "left" },
        { key: "warpWire", label: "Hilo Urdimbre (mm)", align: "right" },
        { key: "weftWire", label: "Hilo Trama (mm)", align: "right" },
        { key: "retention", label: "Retención Nominal (µm)", align: "right" },
        { key: "standard", label: "Norma", align: "right" },
      ],
      rows: [
        {
          id: "row-rd-1",
          mesh: "72 × 15",
          warpWire: "0.28",
          weftWire: "0.45",
          retention: "250 – 300",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-2",
          mesh: "132 × 16",
          warpWire: "0.20",
          weftWire: "0.40",
          retention: "130 – 150",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-3",
          mesh: "152 × 24",
          warpWire: "0.18",
          weftWire: "0.30",
          retention: "90 – 110",
          standard: "ISO 9044",
        },
        {
          id: "row-rd-4",
          mesh: "260 × 40",
          warpWire: "0.10",
          weftWire: "0.15",
          retention: "40 – 50",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-5",
          mesh: "325 × 38",
          warpWire: "0.08",
          weftWire: "0.13",
          retention: "10 – 20",
          standard: "ASTM E2016",
        },
      ],
      caption: "Retención nominal determinada conforme con normas ASTM E2016.",
    },
    features: {
      heading: "Ventajas Operativas",
      intro: "Beneficios de la malla Reverse Dutch en arrastre mecánico y extrusión continua.",
      items: [
        {
          title: "Alta Resistencia al Tiro Longitudinal",
          text: "Los alambres finos orientados en el sentido de tracción resisten el avance de mordazas hidráulicas sin romperse.",
        },
        {
          title: "Estabilidad Transversal",
          text: "Los alambres gruesos transversales evitan el estrechamiento de la cinta bajo presiones de polímeros fundidos.",
        },
        {
          title: "Corte Preciso de Bordes",
          text: "Refilado ultrasónico que produce cantos suaves sin rebabas para avance libre de atascos.",
        },
        {
          title: "Aleaciones Certificadas",
          text: "Aceros inoxidables AISI 304, 304L, 316 y 316L resistentes a degradación térmica y química.",
        },
      ],
    },
    related: {
      heading: "Telas Filtrantes Dutch Relacionadas",
      intro: "Compare otras construcciones dentro de la familia de mallas Dutch.",
      cards: [
        {
          title: "Plain Dutch Weave",
          text: "Hilos de urdimbre gruesos con alambres de trama fina compactada forman poros triangulares uniformes para retención predecible y descarga de torta.",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "Tela Plain Dutch Weave",
          },
          badge: "8×85 – 80×400 (hasta 165×1200)",
          linkLabel: "Ver Plain Dutch",
        },
        {
          title: "Twill Dutch Weave",
          text: "La disposición en sarga duplica la densidad de trama sin paso de luz directo, ofreciendo micro-retención de hasta 1 micra bajo severas presiones diferenciales.",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "Tela Twill Dutch Weave",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "Ver Twill Dutch",
        },
        {
          title: "Resumen de Tela Filtrante",
          text: "Visión global de las opciones de tejido Dutch, aleaciones y métodos de confección industrial.",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Resumen de telas filtrantes",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "Página Principal",
        },
      ],
    },
    cta: {
      id: "reverse-dutch-cta",
      eyebrow: "Cintas Filtrantes Continuas",
      heading: "Consulte con MutualFilter para Mallas Reverse Dutch",
      lead: "Fabricamos cintas cortadas a medida, bobinas continuas y elementos conformados según especificación.",
      secondaryLabel: "Ver Tela Filtrante",
      secondaryTo: "/FilterClothPage",
      note: "Informes de prueba de material disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "ReverseDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Tressengewebe",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Panzertresse (Reverse Dutch Weave)",
      lead: "Durch dichte, feine Kettdrähte und stärkere, beabstandete Schussdrähte bietet die Panzertresse extreme Längszugfestigkeit für kontinuierliche Siebbänder und Hochdruck-Polymerschmelzefiltration.",
      secondaryCta: {
        label: "Filtertuch-Übersicht",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
        alt: "Edelstahl Panzertresse Gewebeoberfläche",
      },
      cardNote: "Längszugfestes Siebband-Medium",
      cardLink: {
        label: "Spezifikationen",
        to: "/ReverseDutchWeavePage#specifications",
      },
      specs: [
        { label: "Maschenbereich", value: "72×15 – 325×38" },
        { label: "Filterfeinheit", value: "10 – 300 µm nominal" },
        { label: "Eigenschaft", value: "Hohe Längszugfestigkeit" },
        { label: "Legierungen", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Zugfeste Endlos-Filterbänder",
      heading: "Umgekehrtes Drahtverhältnis für kontinuierliche Siebwechsler",
      paragraphs: [
        "Die Panzertresse (Reverse Dutch Weave) kehrt das Drahtverhältnis herkömmlicher Tressen um. In dieser Konstruktion werden dichte Feinkettdrähte in Zugrichtung verarbeitet, während stärkere Schussdrähte in größerem Abstand quer verlaufen.",
        "Da die eng aneinanderliegenden Feindrähte in Laufrichtung der Rolle liegen, besitzt das Gewebe eine überragende Reißfestigkeit in Längsrichtung. Diese Eigenschaft macht die Panzertresse zum bevorzugten Filtermedium in automatischen Siebwechslern der Kunststoffextrusion und des Recyclings.",
        "Unter hohem Schmelzedruck verhindern die dicken Querdrähte ein Einschnüren des Bandes, während die dichte Kette Partikel von 10 µm bis 300 µm abscheidet. MutualFilter liefert Endlosbänder mit gratfreien Kanten.",
      ],
      keyPoints: [
        "Dichte Kettendrähte gepaart mit stärkeren Schussdrähten bieten extreme Längszugfestigkeit für kontinuierliche Siebbänder und Hochdruck-Polymerschmelzefiltration.",
        "Verhindert Breiteneinschnürung und Banddehnung unter hohen Vorschubkräften.",
        "Maschenbereich von 72×15 bis 325×38 mit nominellen Filterfeinheiten von 10 µm bis 300 µm.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
        alt: "Detailansicht Panzertresse für Siebwechsler",
      },
    },
    specTable: {
      heading: "Standardspezifikationen Panzertresse",
      intro: "Fertigungsdaten für Edelstahl-Panzertressen nach ASTM E2016 und ISO 9044.",
      columns: [
        { key: "mesh", label: "Maschen (Kette × Schuss)", align: "left" },
        { key: "warpWire", label: "Kett-Draht (mm)", align: "right" },
        { key: "weftWire", label: "Schuss-Draht (mm)", align: "right" },
        { key: "retention", label: "Filterfeinheit (µm)", align: "right" },
        { key: "standard", label: "Norm", align: "right" },
      ],
      rows: [
        {
          id: "row-rd-1",
          mesh: "72 × 15",
          warpWire: "0.28",
          weftWire: "0.45",
          retention: "250 – 300",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-2",
          mesh: "132 × 16",
          warpWire: "0.20",
          weftWire: "0.40",
          retention: "130 – 150",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-3",
          mesh: "152 × 24",
          warpWire: "0.18",
          weftWire: "0.30",
          retention: "90 – 110",
          standard: "ISO 9044",
        },
        {
          id: "row-rd-4",
          mesh: "260 × 40",
          warpWire: "0.10",
          weftWire: "0.15",
          retention: "40 – 50",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-5",
          mesh: "325 × 38",
          warpWire: "0.08",
          weftWire: "0.13",
          retention: "10 – 20",
          standard: "ASTM E2016",
        },
      ],
      caption: "Nominale Filterfeinheit ermittelt nach ASTM E2016.",
    },
    features: {
      heading: "Praxisrelevante Merkmale",
      intro: "Mechanische Vorzüge der Panzertresse im automatischen Extrusionsbetrieb.",
      items: [
        {
          title: "Extreme Längszugfestigkeit",
          text: "Die feinen Längsdrähte nehmen hohe Zugkräfte hydraulischer Vorschubeinheiten bruchfrei auf.",
        },
        {
          title: "Querrippen gegen Einschnürung",
          text: "Starke Schussdrähte stabilisieren die Bandbreite unter Schmelzedruck und verhindern Randleckagen.",
        },
        {
          title: "Präzise Bandkanten",
          text: "Ultraschallbeschnitt sorgt für gratfreie Kanten und störungsfreien Bandtransport.",
        },
        {
          title: "Thermisch beständig",
          text: "Aus AISI 304, 304L, 316 und 316L für dauerhaften Einsatz in heißen Polymerschmelzen.",
        },
      ],
    },
    related: {
      heading: "Verwandte Gewebetypen",
      intro: "Vergleichen Sie die Panzertresse mit weiteren Tressenausführungen.",
      cards: [
        {
          title: "Plain Dutch Weave",
          text: "Starke Kettendrähte und dicht geschlagene feine Schussdrähte bilden eine einheitliche dreieckige Porengeometrie für zuverlässigen Rückhalt und leichten Kuchenabwurf.",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "Glatte Tresse Filtergewebe",
          },
          badge: "8×85 – 80×400 (bis 165×1200)",
          linkLabel: "Zur glatten Tresse",
        },
        {
          title: "Twill Dutch Weave",
          text: "Die köperbindige Kettanordnung verdoppelt die Schussdichte ohne Direktdurchsicht und bietet Feinstfiltration ab 1 Mikrometer unter hohem Differenzdruck.",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "Köpertresse Filtergewebe",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "Zur Köpertresse",
        },
        {
          title: "Filtertuch-Übersicht",
          text: "Umfassende Übersicht über Tressengewebe, Legierungsauswahl und Konfektionsmöglichkeiten.",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Filtertuch Übersicht",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "Übergeordnete Seite",
        },
      ],
    },
    cta: {
      id: "reverse-dutch-cta",
      eyebrow: "Siebbänder für Extrusionsanlagen",
      heading: "Panzertresse nach Maß anfragen",
      lead: "MutualFilter liefert präzise geschnittene Siebbänder und Rollen für Ihre Siebwechsler.",
      secondaryLabel: "Filtertuch-Übersicht",
      secondaryTo: "/FilterClothPage",
      note: "Werksprüfzeugnisse auf Anfrage bei Auftragserteilung erhältlich.",
    },
  },
  fr: {
    breadcrumbKey: "ReverseDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Toile Métallique Reps",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toile Reps Inversé (Reverse Dutch)",
      lead: "Constituée d'une chaîne dense de fils fins et d'une trame robuste espacée, la toile reps inversé offre une résistance longitudinale extrême pour bandes de changeurs continus et filtration sous haute pression de polymères.",
      secondaryCta: {
        label: "Voir Toutes les Toiles",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
        alt: "Toile métallique reps inversé en acier inoxydable",
      },
      cardNote: "Bandes filtrantes à haute résistance à la traction",
      cardLink: {
        label: "Spécifications",
        to: "/ReverseDutchWeavePage#specifications",
      },
      specs: [
        { label: "Plage de Maillage", value: "72×15 – 325×38" },
        { label: "Seuil Nominal", value: "10 – 300 µm nominal" },
        { label: "Propriété Mécanique", value: "Haute résistance à la traction" },
        { label: "Alliages Standard", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Bandes de Filtration Haute Résistance",
      heading: "Ratio de Fils Inversé pour Changeurs Automatiques de Filtres",
      paragraphs: [
        "Le tissage reps inversé (Reverse Dutch ou panzertresse) inverse le rapport classique des fils. Dans cette configuration, un grand nombre de fils de chaîne fins est disposé dans le sens de déroulement de la bande, tandis que des fils de trame plus épais sont insérés transversalement.",
        "Puisque les fils fins compactés s'étendent parallèlement à la longueur du rouleau, la toile offre une résistance à la rupture exceptionnelle dans le sens longitudinal. Cela en fait le support de référence pour changeurs de filtres continus en recyclage et extrusion plastique.",
        "Sous la pression du polymère en fusion, les gros fils de trame agissent comme des nervures rigides qui évitent le rétrécissement en largeur (striction), tandis que la chaîne fine assure une rétention calibrée de 10 µm à 300 µm. MutualFilter fournit des bandes refendues de précision aux bords ébavurés.",
      ],
      keyPoints: [
        "Fils de chaîne denses combinés à des trames plus épaisses pour une résistance longitudinale élevée sur bandes de changeurs continus et filtration de polymères.",
        "Structure inversée prévenant le rétrécissement et l'étirement sous fortes contraintes de traction.",
        "Plage de maillage standard de 72×15 à 325×38 pour des seuils de rétention de 10 µm à 300 µm.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
        alt: "Détail de la toile reps inversé pour changeurs de filtres",
      },
    },
    specTable: {
      heading: "Spécifications Standards Reps Inversé",
      intro: "Paramètres de fabrication pour toiles reps inversé en acier inoxydable selon ASTM E2016 et ISO 9044.",
      columns: [
        { key: "mesh", label: "Maillage (Chaîne × Trame)", align: "left" },
        { key: "warpWire", label: "Fil Chaîne (mm)", align: "right" },
        { key: "weftWire", label: "Fil Trame (mm)", align: "right" },
        { key: "retention", label: "Seuil Nominal (µm)", align: "right" },
        { key: "standard", label: "Norme", align: "right" },
      ],
      rows: [
        {
          id: "row-rd-1",
          mesh: "72 × 15",
          warpWire: "0.28",
          weftWire: "0.45",
          retention: "250 – 300",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-2",
          mesh: "132 × 16",
          warpWire: "0.20",
          weftWire: "0.40",
          retention: "130 – 150",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-3",
          mesh: "152 × 24",
          warpWire: "0.18",
          weftWire: "0.30",
          retention: "90 – 110",
          standard: "ISO 9044",
        },
        {
          id: "row-rd-4",
          mesh: "260 × 40",
          warpWire: "0.10",
          weftWire: "0.15",
          retention: "40 – 50",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-5",
          mesh: "325 × 38",
          warpWire: "0.08",
          weftWire: "0.13",
          retention: "10 – 20",
          standard: "ASTM E2016",
        },
      ],
      caption: "Seuils nominaux conformes aux méthodes d'essai ASTM E2016.",
    },
    features: {
      heading: "Atouts Techniques et Mécaniques",
      intro: "Avantages de la toile reps inversé dans les procédés de traction continue.",
      items: [
        {
          title: "Résistance Maximale à la Traction",
          text: "Les fils de chaîne fins alignés longitudinalement résistent à la traction sans rupture lors de l'avance du ruban.",
        },
        {
          title: "Rigidité Transversale",
          text: "Les fils de trame renforcés maintiennent la largeur de bande constante sous la poussée de la matière fondue.",
        },
        {
          title: "Coupe Nette des Bords",
          text: "Bordures découpées de précision et ébavurées pour un défilement continu sans coincement.",
        },
        {
          title: "Tenue aux Hautes Températures",
          text: "Alliages AISI 304, 304L, 316 et 316L résistant aux contraintes thermiques des polymères chauds.",
        },
      ],
    },
    related: {
      heading: "Autres Toiles Reps",
      intro: "Comparez le reps inversé avec les autres variantes de toiles filtrantes.",
      cards: [
        {
          title: "Plain Dutch Weave",
          text: "Fils de chaîne robustes associés à une trame fine serrée formant des pores triangulaires réguliers pour une rétention prévisible et un démoulage aisé du gâteau.",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "Toile Plain Dutch",
          },
          badge: "8×85 – 80×400 (jusqu'à 165×1200)",
          linkLabel: "Voir Plain Dutch",
        },
        {
          title: "Twill Dutch Weave",
          text: "L'armure sergée permet de doubler la densité de trame sans passage direct de lumière, assurant une filtration fine dès 1 micron sous forte pression différentielle.",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "Toile Twill Dutch",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "Voir Twill Dutch",
        },
        {
          title: "Vue d'ensemble Toiles Filtrantes",
          text: "Présentation globale des armures reps, des critères de sélection et des fabrications sur mesure.",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Vue d'ensemble toiles reps",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "Page Parent",
        },
      ],
    },
    cta: {
      id: "reverse-dutch-cta",
      eyebrow: "Bandes Filtrantes Industrielles",
      heading: "Consultez MutualFilter pour Vos Bandes Reps Inversé",
      lead: "Nous fournissons des rouleaux continus et des bandes prêtes à l'emploi selon vos dimensions.",
      secondaryLabel: "Voir Toiles Filtrantes",
      secondaryTo: "/FilterClothPage",
      note: "Certificats matière disponibles lorsque spécifiés dans la commande.",
    },
  },
  pt: {
    breadcrumbKey: "ReverseDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Tecido Filtrante Holandês",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Tela em Trama Holandesa Reversa",
      lead: "Com fios de urdume finos e compactados cruzados por fios de trama espessos e espaçados, a trama holandesa reversa proporciona extrema resistência à tração longitudinal para fitas contínuas e extrusão sob alta pressão.",
      secondaryCta: {
        label: "Ver Tecidos Filtrantes",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
        alt: "Tela em trama holandesa reversa em aço inoxidável",
      },
      cardNote: "Meio para fitas de alta tração",
      cardLink: {
        label: "Especificações",
        to: "/ReverseDutchWeavePage#specifications",
      },
      specs: [
        { label: "Faixa de Malha", value: "72×15 – 325×38" },
        { label: "Retenção Nominal", value: "10 – 300 µm nominal" },
        { label: "Propriedade", value: "Alta resistência à tração longitudinal" },
        { label: "Ligas Padrão", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Fitas Contínuas de Alta Resistência",
      heading: "Proporção de Fios Invertida para Trocadores Contínuos de Tela",
      paragraphs: [
        "A trama holandesa reversa (Reverse Dutch Weave ou panzertresse) inverte a proporção usual dos fios. Nessa construção, um grande número de fios finos de urdume é posicionado longitudinalmente sob tensão, enquanto fios mais grossos de trama são inseridos transversalmente.",
        "Como os fios finos compactados correm paralelos ao sentido de avanço do rolo, o tecido apresenta excepcional resistência à ruptura longitudinal. Essa característica mecânica torna o tecido holandês reverso o padrão para trocadores contínuos de tela em linhas de reciclagem e compostagem de polímeros.",
        "Sob elevadas pressões do polímero fundido, os fios grossos transversais funcionam como nervuras rígidas que impedem o estreitamento da fita, enquanto o urdume fino garante retenção de 10 µm a 300 µm. A MutualFilter fornece fitas contínuas cortadas com bordas desbastadas.",
      ],
      keyPoints: [
        "Urdumes densos combinados com arames de trama mais grossos proporcionam extrema resistência à tração para fitas contínuas e filtração de polímeros sob alta pressão.",
        "Estrutura invertida que previne o estreitamento da fita sob forte força de arraste.",
        "Faixa padrão de malha de 72×15 a 325×38 para retenções nominais de 10 µm a 300 µm.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
        alt: "Detalhe da trama holandesa reversa para trocadores de tela",
      },
    },
    specTable: {
      heading: "Especificações Padrão Reverse Dutch",
      intro: "Dados industriais de fabricação para telas em trama holandesa reversa em conformidade com ASTM E2016 e ISO 9044.",
      columns: [
        { key: "mesh", label: "Malha (Urdume × Trama)", align: "left" },
        { key: "warpWire", label: "Fio Urdume (mm)", align: "right" },
        { key: "weftWire", label: "Fio Trama (mm)", align: "right" },
        { key: "retention", label: "Retenção Nominal (µm)", align: "right" },
        { key: "standard", label: "Norma", align: "right" },
      ],
      rows: [
        {
          id: "row-rd-1",
          mesh: "72 × 15",
          warpWire: "0.28",
          weftWire: "0.45",
          retention: "250 – 300",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-2",
          mesh: "132 × 16",
          warpWire: "0.20",
          weftWire: "0.40",
          retention: "130 – 150",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-3",
          mesh: "152 × 24",
          warpWire: "0.18",
          weftWire: "0.30",
          retention: "90 – 110",
          standard: "ISO 9044",
        },
        {
          id: "row-rd-4",
          mesh: "260 × 40",
          warpWire: "0.10",
          weftWire: "0.15",
          retention: "40 – 50",
          standard: "ASTM E2016",
        },
        {
          id: "row-rd-5",
          mesh: "325 × 38",
          warpWire: "0.08",
          weftWire: "0.13",
          retention: "10 – 20",
          standard: "ASTM E2016",
        },
      ],
      caption: "Retenção nominal aferida conforme os critérios da ASTM E2016.",
    },
    features: {
      heading: "Destaques Técnicos e Mecânicos",
      intro: "Vantagens estruturais da trama holandesa reversa em processos contínuos de extrusão.",
      items: [
        {
          title: "Alta Resistência ao Repuxo",
          text: "Os fios finos de urdume distribuem o esforço de tração evitando rompimentos nas pinças hidráulicas.",
        },
        {
          title: "Prevenção de Estreitamento",
          text: "A trama reforçada impede que a fita afunile sob a pressão do polímero fundido.",
        },
        {
          title: "Corte Preciso sem Rebarbas",
          text: "Bordas tratadas por ultrassom que garantem alimentação suave sem travamentos mecânicos.",
        },
        {
          title: "Resistência Térmica e Química",
          text: "Aços inoxidáveis AISI 304, 304L, 316 e 316L adequados para processamento em altas temperaturas.",
        },
      ],
    },
    related: {
      heading: "Outras Tramas Filtrantes",
      intro: "Compare a trama holandesa reversa com as outras opções da família.",
      cards: [
        {
          title: "Plain Dutch Weave",
          text: "Fios de urdume grossos combinados com trama fina compactada produzem geometria uniforme de poros triangulares para retenção previsível e fácil descarte de torta.",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "Tecido Plain Dutch Weave",
          },
          badge: "8×85 – 80×400 (até 165×1200)",
          linkLabel: "Ver Plain Dutch",
        },
        {
          title: "Twill Dutch Weave",
          text: "A urdidura em sarja dobra a densidade da trama sem linha de visão direta, garantindo microrretenção a partir de 1 mícron sob pressões diferenciais severas.",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "Tecido Twill Dutch Weave",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "Ver Twill Dutch",
        },
        {
          title: "Visão Geral de Tecidos Filtrantes",
          text: "Panorama geral das tramas holandesas, critérios de seleção de ligas e configurações especiais.",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Tecidos filtrantes visão geral",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "Página Principal",
        },
      ],
    },
    cta: {
      id: "reverse-dutch-cta",
      eyebrow: "Fitas de Filtragem Industrial",
      heading: "Consulte a MutualFilter para Trama Holandesa Reversa",
      lead: "Fornecemos fitas cortadas sob medida e rolos contínuos conforme a largura de sua máquina.",
      secondaryLabel: "Ver Tecidos Filtrantes",
      secondaryTo: "/FilterClothPage",
      note: "Relatórios de ensaio de material disponíveis mediante solicitação no pedido.",
    },
  },
};
